import { mergeClasses } from '@fluentui/react-components'
import type { HTMLAttributes } from 'react'
import { memo, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatMessage } from '../../types'
import {
  DEV_DEBUG_HTML,
  hasBayerStyleClasses,
  openHtmlDebugPopup,
  sanitizeAssistantHtmlForBubble,
  scopeEmbeddedStyles,
  stripWordArtifactsFromUserBubble,
} from '../../utils/html-sanitization'
import { splitStreamingPreviewContent } from '../../utils/streaming-preview'
import { BubbleActions } from './BubbleActions'
import { useChatBubbleStyles } from './ChatBubble.styles'
import { CommentSummaryContent } from './CommentSummaryContent/CommentSummaryContent'

interface ChatBubbleProps {
  message: ChatMessage
  onCopy: (messageId: string) => void
  onEdit?: (messageId: string) => void
  onDelete?: (messageId: string) => void
  onInsert?: (messageId: string) => void
}

// Memoized so hover state changes in the parent don't replace innerHTML and reset table scroll position
const BubbleHtmlContent = memo(
  ({ innerHtml, ...props }: HTMLAttributes<HTMLDivElement> & { innerHtml: string }) => (
    <div
      {...props}
      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
      dangerouslySetInnerHTML={{ __html: innerHtml }}
    />
  )
)

const ChatBubbleBase = ({ message, onCopy, onEdit, onDelete, onInsert }: ChatBubbleProps) => {
  const styles = useChatBubbleStyles()
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const { t } = useTranslation()
  const bubbleScopeId = useId().replace(/:/g, '')

  const isUser = message.role === 'user'
  const isStreaming = message.role === 'assistant' && message.streamState === 'streaming'
  const streamingPreviewContent = message.streamPreviewContent ?? message.content
  // After streaming finishes and insertReviewComments stored at least one Word comment id.
  const hasCommentNavigation =
    !isUser &&
    !isStreaming &&
    message.wordCommentIds?.some((commentId) => commentId !== null) === true

  // User messages (Word paste) → strip Word artifacts (conditional comments and <style>)
  const sanitizedUserContent = useMemo(() => {
    if (!isUser) return ''
    return stripWordArtifactsFromUserBubble(message.content)
  }, [message.content, isUser])

  // Assistant messages → preserve LLM formatting, scope <style> blocks to prevent CSS leaking
  // Short-circuit during streaming: content is empty while chunks arrive via streamPreviewContent
  const sanitizedAssistantHtmlContent = useMemo(() => {
    if (isUser || isStreaming) return ''
    return scopeEmbeddedStyles(sanitizeAssistantHtmlForBubble(message.content), bubbleScopeId)
  }, [message.content, isUser, isStreaming, bubbleScopeId])

  const streamingSections = useMemo(
    () =>
      isStreaming
        ? splitStreamingPreviewContent(streamingPreviewContent)
        : { completedHtml: '', liveText: '' },
    [isStreaming, streamingPreviewContent]
  )

  const sanitizedStreamingAssistantHtmlContent = useMemo(() => {
    if (isUser || !isStreaming || !streamingSections.completedHtml) return ''
    return scopeEmbeddedStyles(
      sanitizeAssistantHtmlForBubble(streamingSections.completedHtml),
      bubbleScopeId
    )
  }, [streamingSections.completedHtml, isUser, isStreaming, bubbleScopeId])

  const renderedHtml = isUser
    ? sanitizedUserContent
    : isStreaming && sanitizedStreamingAssistantHtmlContent
      ? sanitizedStreamingAssistantHtmlContent
      : sanitizedAssistantHtmlContent
  const hasTable = /<table[\s>]/i.test(renderedHtml)

  // Defensive: sanitization pipeline may strip all content (e.g. malformed HTML from LLM)
  // while raw content is non-empty. Fall back to plain text so the message isn't invisible
  const shouldFallbackToPlainAssistantText =
    !isUser &&
    !isStreaming &&
    sanitizedAssistantHtmlContent.length === 0 &&
    message.content.length > 0

  let innerHtml: string | undefined
  let plainText: string | undefined

  if (isUser) {
    innerHtml = sanitizedUserContent
  } else if (isStreaming && sanitizedStreamingAssistantHtmlContent) {
    innerHtml = sanitizedStreamingAssistantHtmlContent
  } else if (shouldFallbackToPlainAssistantText) {
    plainText = message.content
  } else {
    innerHtml = sanitizedAssistantHtmlContent
  }

  const hasAssistantTable = !isUser && hasTable
  const hasBayerClassesOnAssistantMessage = !isUser && hasBayerStyleClasses(renderedHtml)

  const bubbleClassName = mergeClasses(
    styles.bubble,
    isUser ? styles.bubbleUser : styles.bubbleAssistant,
    hasAssistantTable && styles.bubbleAssistantTable,
    hasBayerClassesOnAssistantMessage && !hasAssistantTable && styles.bubbleAssistantBodyText,
    (plainText !== undefined || hasCommentNavigation || !!message.infoNote) && styles.bubblePreWrap
  )

  const bubbleSharedProps: HTMLAttributes<HTMLDivElement> = {
    id: isUser ? undefined : bubbleScopeId,
    className: bubbleClassName,
    'aria-live': isStreaming ? 'polite' : undefined,
    'aria-busy': isStreaming || undefined,
  }

  const bubbleContent =
    hasCommentNavigation && message.wordCommentIds ? (
      <div {...bubbleSharedProps}>
        <CommentSummaryContent
          content={message.content}
          wordCommentIds={message.wordCommentIds}
          bubbleScopeId={bubbleScopeId}
        />
      </div>
    ) : isStreaming && sanitizedStreamingAssistantHtmlContent ? (
      <div {...bubbleSharedProps}>
        <BubbleHtmlContent innerHtml={sanitizedStreamingAssistantHtmlContent} />
        {streamingSections.liveText && <div>{streamingSections.liveText}</div>}
      </div>
    ) : isStreaming ? (
      <div {...bubbleSharedProps}>{streamingSections.liveText}</div>
    ) : plainText !== undefined ? (
      <div {...bubbleSharedProps}>{plainText}</div>
    ) : (
      <BubbleHtmlContent {...bubbleSharedProps} innerHtml={innerHtml ?? ''} />
    )

  const wrapperClassName = mergeClasses(styles.bubbleWrapper, isUser && styles.bubbleWrapperUser)

  return (
    <div
      className={wrapperClassName}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={() => setIsFocused(false)}
      onDoubleClick={
        DEV_DEBUG_HTML && !isUser && !isStreaming
          ? () => openHtmlDebugPopup(message.content)
          : undefined
      }
      role="article"
      aria-label={isUser ? t('chatBubble.userMessage') : t('chatBubble.assistantMessage')}>
      {bubbleContent}
      <BubbleActions
        messageId={message.id}
        messageRole={message.role}
        timestamp={message.timestamp}
        visible={isHovered || isFocused || !isUser}
        sources={message.sources}
        showSources={!isStreaming}
        onCopy={isStreaming ? undefined : onCopy}
        onEdit={isUser && !isStreaming ? onEdit : undefined}
        onDelete={isUser && !isStreaming ? onDelete : undefined}
        onInsert={!isUser && !isStreaming ? onInsert : undefined}
      />
    </div>
  )
}

// Shallow-compare prevents re-render for bubbles whose message reference hasn't changed.
// During streaming only the active assistant bubble re-renders  all others are skipped.
const ChatBubble = memo(ChatBubbleBase)

export { ChatBubble }
